import ModelTypeConstants from 'constants/ModelTypeConstants';
import EntryDAO from 'dao/EntryDAO';
import {BioentitySummary} from 'folder-items/FileConstants';
import {triggerComputedFieldsUpdate} from 'notebook/integrations/registry/RegistrationActionUtil';
import {RegistrationOperation} from 'notebook/registration-tables/RegistrationTableConstants';
import {ACTION_REGISTER} from 'notebook/store/VersioningConstants';
import {
  ActionResultType,
  DoPerformAction,
  performServerSideWriteAction,
  ServerSideWriteActionResult,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import reportUserFacingLockError from 'registry/reportUserFacingLockError';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import EntryStore from 'stores/EntryStore';
import APIUtil from 'util/APIUtil';
import requestWithRetries from 'util/requestWithRetries';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';
import * as StageRunActions from 'workflows/StageRunActions';

export default async function registerSamples({
  entryId,
  registrationTableProvenanceUuid,
  operations,
  rowIndicesToRegister,
}: {
  entryId: string;
  registrationTableProvenanceUuid: string;
  operations: Array<RegistrationOperation>;
  rowIndicesToRegister: Array<number>;
}): Promise<void> {
  const entryType = APIUtil.apiIdToType(entryId);
  const doRegisterSamples: DoPerformAction<{
    fileSummariesById: {[id: string]: BioentitySummary};
    otherAffectedEntryIds: Set<string>;
  }> = async () => {
    const response = await requestWithRetries(
      () =>
        EntryDAO.registerSamples(entryId, registrationTableProvenanceUuid, operations, rowIndicesToRegister),
      {
        onMaxAttemptsExceeded: () => {
          reportUserFacingLockError('client:registerSamples');
        },
      }
    );
    const {affectedStageRuns, stageRunSamples, batchesById, fileSummariesById} = response!;
    const otherAffectedEntryIds = new Set<string>();
    if (entryType === ModelTypeConstants.STAGE_ENTRY) {
      StageRunActions.dispatchStageRunSamplesFetched({
        stageRunSamples,
        batchesById,
        containersById: {},
        fileSummariesById,
      });
      for (const stageRun of affectedStageRuns) {
        if (stageRun.stageEntry.id !== entryId) {
          otherAffectedEntryIds.add(stageRun.stageEntry.id);
        }
      }
    }
    return {resultType: ActionResultType.SUCCESS, responseData: {fileSummariesById, otherAffectedEntryIds}};
  };
  const doPostRefresh = (
    actionResult: ServerSideWriteActionResult<{
      fileSummariesById: {[id: string]: BioentitySummary};
      otherAffectedEntryIds: Set<string>;
    }>
  ): Promise<void> => {
    if (actionResult.resultType === ActionResultType.ABORTED) {
      return Promise.resolve();
    }
    const {fileSummariesById, otherAffectedEntryIds} = actionResult.responseData;
    // Updating computed fields may take a long time, so don't block
    triggerComputedFieldsUpdate({
      entryId,
      provenanceUuid: registrationTableProvenanceUuid,
      bioentityIds: Object.keys(fileSummariesById),
    });
    if (entryType === ModelTypeConstants.STAGE_ENTRY) {
      StageEntryActions.refresh(otherAffectedEntryIds);
    }
    return Promise.resolve();
  };
  await performServerSideWriteAction({
    entryId,
    loadingScreenMessage: 'Registering...',
    autoSaverAction: ACTION_REGISTER,
    doPerformAction: doRegisterSamples,
    doPostRefresh,
    analyticsActionForPerfTracking: 'registration_table_register',
  });
}
